import type { ApprovalBasis, ApprovalFormField } from '../../types/approval';
import { selectLabel } from '../../lib/approval-select';
import { formatDateTime } from '../../lib/base-i18n';
import './ApprovalForm.css';

/**
 * 채운 값 보기 · 「이 문서가 쓴 기준」 (2026-08-28 · 자리 재정의 판 9).
 *
 * ★★ **칸 정의는 «그때의 것»으로 그린다.** 서버가 그 판정을 해서 준다(스냅샷의 정의 →
 *    없으면 지금 종류의 서식). 지금 서식으로 옛 문서를 그리면 칸이 사라지거나 이름이 달라져
 *    「이 값이 무슨 칸이었나」에 답할 수 없게 된다.
 * ★ 옛 문서는 정의도 값도 없다 — 그때는 **아무것도 그리지 않는다**(본문만 남는 종전 모습).
 */

export function ApprovalFormView({ fields, data }: {
  fields: ApprovalFormField[];
  data: Record<string, unknown> | null | undefined;
}) {
  if (fields.length === 0 || !data) return null;
  // 값이 하나도 없으면 빈 표를 세우지 않는다 — 빈 칸만 늘어선 표는 읽을 것이 없다.
  const rows = fields.filter((f) => data[f.key] !== undefined && data[f.key] !== null && data[f.key] !== '');
  if (rows.length === 0) return null;

  return (
    <section className="apv" aria-label="서식">
      <h3>서식</h3>
      {rows.map((f) => (
        <div className="apv__row" key={f.key}>
          <span className="apv__k">{f.label}</span>
          <span className="apv__v">{renderValue(f, data[f.key])}</span>
        </div>
      ))}
    </section>
  );
}

function renderValue(field: ApprovalFormField, v: unknown) {
  /**
   * ★★ **계산식은 «그때 센 값»을 그대로 보여 준다** (2026-09-01 · 판 13).
   *    여기서 다시 세면 안 된다 — 문서는 기안 시점의 서식·값을 이고 있고, 지금 다시 세면
   *    「그때 얼마였나」가 아니라 「지금 세면 얼마인가」를 적게 된다.
   * ★ 식을 곁에 남긴다 — 「이 숫자가 어디서 왔나」에 답하는 자리다.
   */
  if (field.kind === 'formula') {
    const n = typeof v === 'number' ? v : Number(v);
    const d = field.decimals ?? 0;
    const num = Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })
      : '—';
    const unit = field.format === 'money' ? ' 원' : field.format === 'hours' ? ' 시간' : '';
    return (
      <>
        <b>{num}{unit}</b>
        {field.expr ? <code className="apv__expr">{field.expr}</code> : null}
      </>
    );
  }
  if (field.kind === 'period') {
    const p = (typeof v === 'object' && v !== null ? v : {}) as { start?: string; end?: string };
    return `${p.start ?? ''} ~ ${p.end ?? ''}`;
  }
  if (field.kind === 'table' && Array.isArray(v)) {
    const cols = field.columns ?? [];
    return (
      <table className="apv__tbl">
        <thead><tr>{cols.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
        <tbody>
          {(v as unknown[]).map((row, i) => (
            <tr key={i}>
              {/* ★ 고르기 열은 짝이라 그냥 찍으면 `[object Object]` 다 — 이름으로 그린다(예산 판 B0). */}
              {cols.map((c, ci) => <td key={c}>{selectLabel((row as unknown[])?.[ci])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  /**
   * ★★★ **고르기는 «그때의 이름»을 그린다** (2026-09-04 · 예산 판 B0).
   *    값이 `{code,label}` 이라 그냥 찍으면 `[object Object]` 가 된다.
   *    이름을 쓰는 이유 — 회사가 나중에 목록의 이름을 고쳐도 **이 문서는 안 바뀐다**.
   * ★ 옛 문서는 이름 문자열이다 — 같은 함수가 그대로 그린다.
   */
  if (field.kind === 'select') return selectLabel(v);
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
}

const FINALIZE_LABEL: Record<string, string> = {
  NONE: '전결 없음', DAYS: '일수 기준', AMOUNT: '금액 기준',
};

/**
 * 「이 문서가 쓴 기준」 — **접어 둔다.**
 *
 * ★★ 평소에는 볼 일이 없고, 「왜 이렇게 됐나」를 물을 때만 펼친다.
 * ★★ 여기 적힌 것은 **그때의 값**이다 — 그 뒤에 직위가 바뀌고 기준이 바뀌어도 여기는 안 변한다.
 *    그것이 이 칸이 있는 이유다(지금 값을 보여 주면 반년 뒤 다른 설명이 나온다).
 * ★ 옛 문서·임시보관에는 없다 → 아무것도 그리지 않는다.
 */
export function ApprovalBasisPanel({ basis }: { basis: ApprovalBasis | null | undefined }) {
  if (!basis) return null;
  const f = basis.docType.finalize;
  const drafterPlace = [basis.drafter.division, basis.drafter.team].filter(Boolean).join(' · ');

  return (
    <details className="apb">
      <summary className="apb__sum">
        이 문서가 쓴 기준
        {/*
          ⚠ 2026-10-06 — 이 자리는 ★ 로케일조차 «없었다»(`toLocaleString()` 맨손).
            ⇒ ⛔ 그래서 글자가 ★ «브라우저» 설정을 따랐다 — 앱이 한국어인데 브라우저가 영어면
              ⭐ 이 줄만 영어로 보였다. ⇒ ★ 그러므로 이 자리는 ⭐ 자정 말고도 «더» 바뀐다.
        */}
        <span className="apb__when">{formatDateTime(basis.at)} 기준</span>
      </summary>
      <div className="apb__body">
        <div className="apb__grp">
          <span className="apb__gl">전결</span>
          <span className="apb__gv">
            {FINALIZE_LABEL[f.kind] ?? f.kind}
            {f.kind !== 'NONE' && f.threshold !== null && ` · ${f.threshold.toLocaleString()} 이하면 ${f.steps}단계까지`}
            {basis.docType.source === 'CODE' && ' · 회사 설정 없이 기본값으로'}
          </span>
        </div>

        <div className="apb__grp">
          <span className="apb__gl">기안자</span>
          <span className="apb__gv">
            {basis.drafter.name}
            {basis.drafter.position && ` · ${basis.drafter.position}`}
            {drafterPlace && ` · ${drafterPlace}`}
          </span>
        </div>

        <div className="apb__grp">
          <span className="apb__gl">결재선</span>
          <span className="apb__gv">
            {basis.approvers.map((a) => `${a.name}${a.position ? ` ${a.position}` : ''}`).join(' → ') || '—'}
          </span>
        </div>

        <p className="apb__note">
          여기 적힌 직위 · 소속 · 전결 기준은 <strong>상신할 때의 값</strong>입니다.
          그 뒤에 바뀌어도 이 칸은 바뀌지 않습니다 — 「그때 왜 이렇게 됐나」에 답하기 위한 기록입니다.
        </p>
      </div>
    </details>
  );
}
